import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiRecipientShow, Recipient } from "../lib/api";

export default function RecipientShow() {
  const { id } = useParams();
  const [recipient, setRecipient] = useState<Recipient | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await apiRecipientShow(Number(id));
        setRecipient(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="page py-6">
        <div className="max-w-3xl mx-auto">
          <div className="section-box p-4 space-y-3">
            <div className="skeleton h-4 w-32" />
            <div className="skeleton h-4 w-48" />
            <div className="skeleton h-4 w-40" />
          </div>
        </div>
      </div>
    );
  }

  if (!recipient) {
    return (
      <div className="page py-6">
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="alert-danger text-sm">
            Unable to load recipient. Please go back and try again.
          </div>
          <Link to="/recipients" className="btn btn-white text-sm">
            ← Back to Recipients
          </Link>
        </div>
      </div>
    );
  }

  // Support both old shape (account) and new shape (accounts[0])
  const primaryAccount: any =
    (recipient as any).account ??
    (recipient.accounts && recipient.accounts.length > 0
      ? recipient.accounts[0]
      : null) ??
    {};

  const currency =
    recipient.currency ?? primaryAccount?.currency ?? "-" as string;

  return (
    <div className="page py-6">
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="title-page">Recipient details</h1>
            <p className="text-sm text-gray-500 truncate">
              {recipient.name}
            </p>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2">
            <Link
              to={`/recipients/${recipient.id}/edit`}
              className="btn btn-white text-sm"
            >
              Edit
            </Link>
          </div>
        </div>

        {/* Details card */}
        <div className="section-box p-4 space-y-4 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
            <DetailRow label="Name" value={recipient.name} />
            {recipient.full_address && (
              <DetailRow label="Address" value={recipient.full_address} />
            )}
            <DetailRow
              label="Type"
              value={recipient.type ? recipient.type.toUpperCase() : "-"}
            />
            <DetailRow label="Currency" value={currency} />
          </div>

          <hr className="border-gray-100" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
            <DetailRow label="IBAN" value={primaryAccount?.iban ?? "-"} />
            <DetailRow
              label="Account Number"
              value={primaryAccount?.account_number ?? "-"}
            />
            <DetailRow
              label="Bank Name"
              value={primaryAccount?.bank_name ?? "-"}
            />
            <DetailRow
              label="Bank BIC"
              value={primaryAccount?.bank_bic ?? "-"}
            />
            <DetailRow
              label="Bank Address"
              value={primaryAccount?.bank_address ?? "-"}
            />
            <DetailRow
              label="Routing Number"
              value={primaryAccount?.routing_number ?? "-"}
            />
            <DetailRow
              label="Bank Country"
              value={primaryAccount?.bank_country ?? "-"}
            />
            <DetailRow
              label="Intermediary Bank Name"
              value={primaryAccount?.intermediary_bank_name ?? "-"}
            />
            <DetailRow
              label="Intermediary Bank BIC"
              value={primaryAccount?.intermediary_bank_bic ?? "-"}
            />
          </div>
        </div>

        <div>
          <Link to="/recipients" className="btn btn-white text-sm">
            ← Back to Recipients
          </Link>
        </div>
      </div>
    </div>
  );
}

type DetailRowProps = {
  label: string;
  value: string;
};

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <p className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2">
      <span className="font-medium text-gray-900">{label}:</span>
      <span className="text-gray-700 break-all">{value || "-"}</span>
    </p>
  );
}
